"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, type MutableRefObject, type UIEventHandler } from "react";
import type { TypedNotification } from "@/types/feed.types";
import { NEAR_TOP_SCROLL_THRESHOLD_PX } from "../../constants";
import { STICK_TO_BOTTOM_SCROLL_THRESHOLD_PX } from "../utils/constants";
interface UseNotificationsScrollParams {
  readonly items: TypedNotification[];
  readonly isAuthenticated: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly showLoader: boolean;
  readonly showNoItems: boolean;
  readonly showErrorState: boolean;
  readonly activeFilterKey: string;
}
interface UseNotificationsScrollResult {
  readonly scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  readonly handleScroll: UIEventHandler<HTMLDivElement>;
}
export const useNotificationsScroll = t0 => {
  const $ = _c(31);
  const {
    items,
    isAuthenticated,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    showLoader,
    showNoItems,
    showErrorState,
    activeFilterKey
  } = t0;
  const scrollContainerRef = useRef(null);
  const hasInitializedScrollRef = useRef(false);
  const isPinnedToBottomRef = useRef(true);
  const isPrependingRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const lastMeasuredScrollHeightRef = useRef(0);
  const shouldEnableInfiniteScroll = isAuthenticated && !showLoader && !showNoItems && !showErrorState;
  let t1;
  if ($[0] !== fetchNextPage || $[1] !== hasNextPage || $[2] !== isAuthenticated || $[3] !== isFetchingNextPage) {
    t1 = () => {
      if (!isAuthenticated) {
        return;
      }
      if (isFetchingNextPage) {
        return;
      }
      if (!hasNextPage) {
        return;
      }
      const container = scrollContainerRef.current;
      if (container) {
        previousScrollHeightRef.current = container.scrollHeight;
      }
      isPrependingRef.current = true;
      fetchNextPage();
    };
    $[0] = fetchNextPage;
    $[1] = hasNextPage;
    $[2] = isAuthenticated;
    $[3] = isFetchingNextPage;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const triggerFetchOlder = t1;
  let t2;
  if ($[5] !== items.length) {
    t2 = () => {
      const scrollElement = scrollContainerRef.current;
      if (!scrollElement) {
        return;
      }
      if (items.length === 0) {
        return;
      }
      if (!hasInitializedScrollRef.current) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
        hasInitializedScrollRef.current = true;
        isPinnedToBottomRef.current = true;
      }
    };
    $[5] = items.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== items) {
    t3 = [items];
    $[7] = items;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useLayoutEffect(t2, t3);
  let t4;
  let t5;
  if ($[9] !== items.length) {
    t4 = () => {
      if (items.length === 0) {
        hasInitializedScrollRef.current = false;
        isPrependingRef.current = false;
        previousScrollHeightRef.current = 0;
        isPinnedToBottomRef.current = true;
      }
    };
    t5 = [items.length];
    $[9] = items.length;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      hasInitializedScrollRef.current = false;
      isPrependingRef.current = false;
      isPinnedToBottomRef.current = true;
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== activeFilterKey) {
    t7 = [activeFilterKey];
    $[13] = activeFilterKey;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (!isPrependingRef.current) {
        return;
      }
      const container_0 = scrollContainerRef.current;
      if (!container_0) {
        isPrependingRef.current = false;
        return;
      }
      const delta = container_0.scrollHeight - previousScrollHeightRef.current;
      if (delta !== 0) {
        container_0.scrollTop = container_0.scrollTop + delta;
      }
      isPrependingRef.current = false;
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== items) {
    t9 = [items];
    $[16] = items;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const scrollElement_0 = scrollContainerRef.current;
      if (!scrollElement_0) {
        return;
      }
      const observationTarget = scrollElement_0.firstElementChild as HTMLElement | null ?? scrollElement_0;
      let rafId = null;
      const scheduleStickToBottom = () => {
        if (!hasInitializedScrollRef.current || !isPinnedToBottomRef.current) {
          return;
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          const container_1 = scrollContainerRef.current;
          if (!container_1) {
            return;
          }
          container_1.scrollTop = container_1.scrollHeight;
          rafId = null;
        });
      };
      if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
          scheduleStickToBottom();
        });
        resizeObserver.observe(observationTarget);
        return () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }
          resizeObserver.disconnect();
        };
      }
      const intervalId = globalThis.setInterval(() => {
        const container_2 = scrollContainerRef.current;
        if (!container_2) {
          return;
        }
        if (!hasInitializedScrollRef.current || !isPinnedToBottomRef.current) {
          return;
        }
        const currentScrollHeight = container_2.scrollHeight;
        if (currentScrollHeight !== lastMeasuredScrollHeightRef.current) {
          lastMeasuredScrollHeightRef.current = currentScrollHeight;
          scheduleStickToBottom();
        }
      }, 250);
      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        globalThis.clearInterval(intervalId);
      };
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== items || $[20] !== showErrorState || $[21] !== showLoader || $[22] !== showNoItems) {
    t11 = [items, showErrorState, showLoader, showNoItems];
    $[19] = items;
    $[20] = showErrorState;
    $[21] = showLoader;
    $[22] = showNoItems;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[24] !== hasNextPage || $[25] !== isFetchingNextPage || $[26] !== shouldEnableInfiniteScroll || $[27] !== triggerFetchOlder) {
    t12 = event => {
      const container_3 = event.currentTarget;
      const distanceFromBottom = container_3.scrollHeight - container_3.scrollTop - container_3.clientHeight;
      const isNearBottom = distanceFromBottom <= STICK_TO_BOTTOM_SCROLL_THRESHOLD_PX;
      if (isNearBottom) {
        isPinnedToBottomRef.current = true;
      } else {
        if (distanceFromBottom > STICK_TO_BOTTOM_SCROLL_THRESHOLD_PX) {
          isPinnedToBottomRef.current = false;
        }
      }
      if (!shouldEnableInfiniteScroll) {
        return;
      }
      if (container_3.scrollTop <= NEAR_TOP_SCROLL_THRESHOLD_PX) {
        if (!isFetchingNextPage && hasNextPage) {
          triggerFetchOlder();
        }
      }
    };
    $[24] = hasNextPage;
    $[25] = isFetchingNextPage;
    $[26] = shouldEnableInfiniteScroll;
    $[27] = triggerFetchOlder;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const handleScroll = t12;
  let t13;
  if ($[29] !== handleScroll) {
    t13 = {
      scrollContainerRef,
      handleScroll
    };
    $[29] = handleScroll;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
};